// src/components/chat/MessageCoachBar.jsx
//
// Shown at the bottom of a team_announcement channel for users
// who can't post (parents / read-only members). Provides one-tap
// access to start a DM with each coach in the channel, instead
// of forcing the parent to navigate Messages -> New Message ->
// pick coach (4 clicks).
//
// Pattern matches Discord/Slack: DM affordances live where the
// user is, not behind navigation.

import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { startOrOpenDm } from '@/services/chatStartDm'
import {
  ChatBubbleLeftRightIcon,
  ArrowPathIcon,
  UserIcon,
} from '@heroicons/react/24/outline'

const HEAD_FONT = "'Barlow Condensed', sans-serif"

export default function MessageCoachBar({
  chat,            // the team_announcement chat doc
  me,              // { uid, displayName, photoURL }
}) {
  const navigate = useNavigate()
  const [openingUid, setOpeningUid] = useState(null)

  // Pull coaches (admin role) out of the channel's members map,
  // excluding the current user (you can't DM yourself).
  const coaches = useMemo(() => {
    const out = []
    const members = chat?.members || {}
    Object.entries(members).forEach(([memberUid, m]) => {
      if (memberUid === me?.uid) return
      if (m?.role !== 'admin') return
      out.push({
        uid: memberUid,
        displayName: m.displayName || 'Coach',
        photoURL: m.photoURL || null,
      })
    })
    // Alphabetical so the order is stable
    return out.sort((a, b) => a.displayName.localeCompare(b.displayName))
  }, [chat?.members, me?.uid])

  const handleMessageCoach = async (coach) => {
    if (!me?.uid) return
    setOpeningUid(coach.uid)
    try {
      const chatId = await startOrOpenDm({ me, other: coach })
      navigate(`/messages/${chatId}`)
    } catch (err) {
      console.error('[MessageCoachBar] failed to start DM:', err?.code, err?.message)
      toast.error(err?.message || 'Could not start chat. Try again.')
      setOpeningUid(null)
    }
  }

  if (coaches.length === 0) {
    // Edge case: parent in a channel with no coaches (shouldn't
    // happen in practice but let's be defensive).
    return (
      <div className="bg-white border-t border-zinc-200 px-4 pt-3 pb-[calc(0.75rem+64px+env(safe-area-inset-bottom))] md:pb-3 sticky bottom-0">
        <div className="max-w-2xl mx-auto rounded-xl bg-zinc-50 border border-zinc-200 px-4 py-3 text-center">
          <p className="text-xs text-zinc-500">
            Only coaches post in this announcement channel.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white border-t border-zinc-200 px-4 pt-3 pb-[calc(0.75rem+64px+env(safe-area-inset-bottom))] md:pb-3 sticky bottom-0">
      <div className="max-w-2xl mx-auto">
        <p
          className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-2 px-1"
          style={{ fontFamily: HEAD_FONT }}
        >
          {coaches.length === 1 ? 'Need to reach the coach?' : 'Need to reach a coach?'}
        </p>
        <div className="flex flex-wrap gap-2">
          {coaches.map((coach) => {
            const isOpening = openingUid === coach.uid
            return (
              <button
                key={coach.uid}
                type="button"
                onClick={() => handleMessageCoach(coach)}
                disabled={isOpening || !me?.uid}
                className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-3 py-2 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                {coach.photoURL ? (
                  <img
                    src={coach.photoURL}
                    alt=""
                    className="h-5 w-5 rounded-full object-cover -ml-1"
                  />
                ) : (
                  <span className="h-5 w-5 rounded-full bg-[#0d1f14]/15 flex items-center justify-center -ml-1">
                    <UserIcon className="h-3 w-3 text-[#0d1f14]" />
                  </span>
                )}
                <span className="normal-case">Message {coach.displayName.split(' ')[0]}</span>
                {isOpening
                  ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                  : <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />}
              </button>
            )
          })}
        </div>
        <p className="text-[10px] text-zinc-400 mt-2 px-1">
          Opens a private 1:1 chat. Existing chats reopen instead of creating duplicates.
        </p>
      </div>
    </div>
  )
}
